import { useTranslate } from "@refinedev/core";
import { Flex, Typography, GlobalToken } from "antd";
import { PlayIcon } from "@phosphor-icons/react";
import { ReactNode } from "react";
import styled from "styled-components";
import { Card, CardTitle, radius } from "@libertify/ui";
import { useAntTheme } from "hooks/useAntTheme";
import { LessonLabel } from "./LessonLabel";
import { formatLessonNumber } from "../formatLessonNumber";

type ReadyTutorialCardProps = Readonly<{
  status: "ready";
  lessonNumber: number;
  title: string;
  description?: string;
  thumbnailUrl: string;
  onClick?: () => void;
}>;

// No video yet, at one of two stages: someone is actively producing it
// ("preparing"), or it's on the roadmap but nobody's started it yet
// ("notStarted").
export type NotReadyTutorialStatus = "preparing" | "notStarted";

type NotReadyTutorialCardProps = Readonly<{
  status: NotReadyTutorialStatus;
  title: string;
  description: string;
  icon: ReactNode;
}>;

export type TutorialCardProps =
  | ReadyTutorialCardProps
  | NotReadyTutorialCardProps;

const NOT_READY_BADGE_TONE: Record<
  NotReadyTutorialStatus,
  { bg: (token: GlobalToken) => string; color: (token: GlobalToken) => string }
> = {
  preparing: {
    bg: (token) => token.colorSuccessBg,
    color: (token) => token.colorSuccessText,
  },
  notStarted: {
    bg: (token) => token.colorFillSecondary,
    color: (token) => token.colorTextSecondary,
  },
};

export const TutorialCard = (props: TutorialCardProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  if (props.status === "ready") {
    const { lessonNumber, title, description, thumbnailUrl, onClick } = props;
    const paddedNumber = formatLessonNumber(lessonNumber);

    return (
      <Card
        padding="spacious"
        shadow="none"
        hoverable={!!onClick}
        onClick={onClick}
        style={{ height: "100%" }}
        cover={
          <StyledThumbnail>
            <StyledThumbnailImage
              src={thumbnailUrl}
              alt={t("pages.tutorials.videoThumbnailAlt", { title })}
              loading="lazy"
            />
            <StyledScrim />
            <StyledLessonNumber>{paddedNumber}</StyledLessonNumber>
            <StyledPlayButton align="center" justify="center">
              <PlayIcon weight="fill" size={22} />
            </StyledPlayButton>
          </StyledThumbnail>
        }
      >
        <Flex vertical gap={4}>
          <LessonLabel lessonNumber={lessonNumber} />
          {/* Clamped to two lines; the native tooltip shows the full title. */}
          <CardTitle title={title}>{title}</CardTitle>
          {description ? <Typography.Text>{description}</Typography.Text> : null}
        </Flex>
      </Card>
    );
  }

  const tone = NOT_READY_BADGE_TONE[props.status];

  return (
    <StyledPendingCard
      $token={theme}
      padding="spacious"
      shadow="none"
    >
      <Flex justify="space-between" align="center">
        <StyledPendingIcon $token={theme}>{props.icon}</StyledPendingIcon>
        <StyledPendingBadge
          $bg={tone.bg(theme)}
          $color={tone.color(theme)}
          $token={theme}
        >
          {t(`pages.tutorials.status.${props.status}`)}
        </StyledPendingBadge>
      </Flex>
      <CardTitle title={props.title} style={{ marginTop: theme.marginMD }}>
        {props.title}
      </CardTitle>
      <Typography.Text>{props.description}</Typography.Text>
    </StyledPendingCard>
  );
};

// ─── Styled components ───

const StyledThumbnail = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
`;

const StyledThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// Darkens the thumbnail so the white overlay text/icons stay legible
// regardless of the underlying image.
const StyledScrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.35) 100%
  );
`;

const StyledLessonNumber = styled.span`
  position: absolute;
  right: 12px;
  bottom: 4px;
  font-size: 128px;
  font-weight: 800;
  line-height: 1;
  color: rgba(255, 255, 255, 0.25);
`;

const StyledPlayButton = styled(Flex)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 56px;
  height: 56px;
  border-radius: ${radius.circle};
  background: rgba(255, 255, 255, 0.25);
  border: 1.5px solid rgba(255, 255, 255, 0.8);
  color: #fff;
`;

const StyledPendingCard = styled(Card)<{ $token: GlobalToken }>`
  height: 100%;
  border-style: dashed;
  border-color: ${({ $token }) => $token.colorBorder};
  background: transparent;
`;

const StyledPendingIcon = styled.div<{ $token: GlobalToken }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: ${radius.box}px;
  border: ${({ $token }) => $token.lineWidth}px solid
    ${({ $token }) => $token.colorBorderSecondary};
  background: ${({ $token }) => $token.colorBgContainer};
  color: ${({ $token }) => $token.colorTextTertiary};
  font-size: 28px;
`;

const StyledPendingBadge = styled.span<{
  $token: GlobalToken;
  $bg: string;
  $color: string;
}>`
  padding: ${({ $token }) => `${$token.paddingXXS}px ${$token.paddingSM}px`};
  border-radius: ${({ $token }) => $token.borderRadiusLG}px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;
